import { Card, Colors, H1, H5 } from "@blueprintjs/core";
import { css } from "glamor";
import * as _ from "lodash";
import * as React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Fight, Juror } from "src/Interfaces";
import Loading from "src/Loading";
import {
	addJurorToFight,
	deleteJurorFromFight,
	getAllFights,
	getFight,
	getJurors,
	sendJurorOrderBackend,
} from "../api";

const styles = {
	container: css({
		width: "100%",
		display: "flex",
		flexWrap: "wrap",
		margin: "20px 0",
	}),
	element: css({ minWidth: "300px", margin: "10px" }),
	title: css({ textAlign: "left" }),
	jurorListElement: css({ textAlign: "center" }),
	dragNDropContent: css({ flexGrow: 1 }),
};

interface State {
	fights: Fight[];
	items: any[];
	selected: any[];
	allJurors: Juror[];
}

const grid = 10;

const getItemStyle = (isDragging: boolean, draggableStyle: any) => {
	return {
		userSelect: "none",
		padding: grid * 2,
		margin: `0 0 ${grid}px 0`,
		background: isDragging ? Colors.GRAY1 : Colors.GRAY2,
		...draggableStyle,
	};
};

const getListStyle = (isDraggingOver: boolean) => ({
	background: isDraggingOver ? Colors.LIGHT_GRAY4 : Colors.LIGHT_GRAY5,
	padding: grid,
	width: "100%",
});

const getOrder = (fight: Fight) => {
	const jurorOrder = Object.values(fight.jurors).map((juror) => ({
		juror_id: juror.id,
		position: juror.position,
	}));
	return { fight_id: fight.id, jurorOrder };
};

export default class JuryPlanManager extends React.Component<{}, State> {
	public state = {
		fights: [] as Fight[],
		items: [],
		selected: [],
		allJurors: [] as Juror[],
	};
	public async componentDidMount() {
		const fightList = await getAllFights();
		const fights = await Promise.all(fightList.map((fight) => getFight(fight.id)));
		const allJurors = await getJurors();
		this.setState({ allJurors, fights });
	}

	private jurorNotInRound(juror: Juror, round: number) {
		let assignedJurors = [] as Juror[];
		this.state.fights
			.filter((fight) => fight.fightnr === round)
			.forEach((fight) => (assignedJurors = assignedJurors.concat(Object.values(fight.jurors))));

		if (assignedJurors.findIndex((assignedJuror) => juror.id === assignedJuror.id) === -1) {
			return 1;
		}
		return 0;
	}

	private onDragEnd = async (result: any, round: number) => {
		const { source, destination } = result;
		// dropped outside the list
		if (!destination) {
			return;
		}

		const oldPosition = source.index + 1;
		const newPosition = destination.index + 1;

		const fights = [...this.state.fights];
		if (
			source.droppableId.startsWith("unassigned") &&
			destination.droppableId.startsWith("unassigned")
		) {
			return;
		} else if (source.droppableId.startsWith("unassigned")) {
			console.log("move from unassigned");
			const destinationFightIndex = this.state.fights.findIndex(
				(_fight) => _fight.name === destination.droppableId
			)!;
			const destinationFight = JSON.parse(JSON.stringify(this.state.fights[destinationFightIndex]));
			const backupDestinationFight = JSON.parse(JSON.stringify(destinationFight));

			const unassignedJurors = this.state.allJurors.filter((juror) =>
				this.jurorNotInRound(juror, round)
			);

			const jurorToMove = unassignedJurors[source.index];
			const destinationJurorKeys = Object.keys(destinationFight.jurors);
			destinationJurorKeys.forEach((key) => {
				const juror = destinationFight.jurors[key];
				if (juror.position >= newPosition) {
					destinationFight.jurors[key].position = destinationFight.jurors[key].position + 1;
				}
			});

			jurorToMove.position = newPosition;
			destinationFight.jurors[jurorToMove.id] = jurorToMove;
			fights[destinationFightIndex] = destinationFight;
			this.setState({ fights });
			const destinationOrder = getOrder(destinationFight);
			try {
				const resAdd = await addJurorToFight(
					parseInt(destinationFight.id, 10),
					jurorToMove.id,
					jurorToMove.position
				);
				console.log(resAdd);
				if (resAdd.success !== true) {
					throw new Error("not successfull");
				} else {
					const resOrder = await sendJurorOrderBackend(destinationOrder);
					if (resOrder.success !== true) {
						throw new Error("not successfull");
					}
				}
			} catch (e) {
				// tslint:disable-next-line:no-console
				console.log(e, "reverting to old juror Order");
				fights[destinationFightIndex] = backupDestinationFight;
				this.setState({ fights });
			}
			return;
		} else if (destination.droppableId.startsWith("unassigned")) {
			console.log("move to unassigned");
			const sourceFightIndex = this.state.fights.findIndex(
				(_fight) => _fight.name === source.droppableId
			)!;
			const sourceFight = JSON.parse(JSON.stringify(this.state.fights[sourceFightIndex]));
			const backupSourceFight = JSON.parse(JSON.stringify(sourceFight));

			let jurorToMove = sourceFight.jurors[result.draggableId];
			const sourceJurorKeys = Object.keys(sourceFight.jurors);
			sourceJurorKeys.forEach((key) => {
				const juror = sourceFight.jurors[key];
				if (juror.position > newPosition) {
					sourceFight.jurors[key].position = sourceFight.jurors[key].position - 1;
				} else if (juror.id === parseInt(result.draggableId, 10)) {
					console.log("found juror");
					jurorToMove = juror;
				}
			});

			delete sourceFight.jurors[jurorToMove.id];
			fights[sourceFightIndex] = sourceFight;
			this.setState({ fights });
			const sourceOrder = getOrder(sourceFight);
			try {
				const resDelete = await deleteJurorFromFight(sourceFight.id, jurorToMove.id);
				if (resDelete.success !== true) {
					throw new Error("not successfull");
				} else {
					if (sourceOrder.jurorOrder.length > 0) {
						const resOrder = await sendJurorOrderBackend(sourceOrder);
						if (resOrder.success !== true) {
							throw new Error("not successfull");
						}
					}
				}
			} catch (e) {
				// tslint:disable-next-line:no-console
				console.log(e, "reverting to old juror Order");
				fights[sourceFightIndex] = backupSourceFight;
				this.setState({ fights });
			}
			return;
		} else {
			const sourceFightIndex = this.state.fights.findIndex(
				(_fight) => _fight.name === source.droppableId
			)!;
			const sourceFight = JSON.parse(JSON.stringify(this.state.fights[sourceFightIndex]));
			const backupSourceFight = JSON.parse(JSON.stringify(sourceFight));
			const destinationFightIndex = this.state.fights.findIndex(
				(_fight) => _fight.name === destination.droppableId
			)!;
			const destinationFight = JSON.parse(JSON.stringify(this.state.fights[destinationFightIndex]));
			const backupDestinationFight = JSON.parse(JSON.stringify(destinationFight));

			if (source.droppableId === destination.droppableId) {
				const jurorKeys = Object.keys(sourceFight.jurors);

				if (oldPosition === newPosition) {
					return;
				} else if (oldPosition < newPosition) {
					jurorKeys.forEach((key) => {
						const juror = sourceFight.jurors[key];
						if (juror.position === oldPosition) {
							sourceFight.jurors[key].position = newPosition;
						} else if (juror.position > oldPosition && juror.position <= newPosition) {
							sourceFight.jurors[key].position = sourceFight.jurors[key].position - 1;
						}
					});
				} else if (oldPosition > newPosition) {
					jurorKeys.forEach((key) => {
						const juror = sourceFight.jurors[key];
						if (juror.position === oldPosition) {
							sourceFight.jurors[key].position = newPosition;
						} else if (juror.position < oldPosition && juror.position >= newPosition) {
							sourceFight.jurors[key].position = sourceFight.jurors[key].position + 1;
						}
					});
				}
				fights[sourceFightIndex] = sourceFight;
				this.setState({ fights });

				const sourceOrder = getOrder(sourceFight);
				try {
					const res = await sendJurorOrderBackend(sourceOrder);
					console.log("sending juror order");
					if (res.success !== true) {
						throw new Error("not successfull");
					}
				} catch (e) {
					// tslint:disable-next-line:no-console
					console.log(e, "reverting to old juror Order");
					fights[sourceFightIndex] = backupSourceFight;
					this.setState({ fights });
				}
			} else {
				const sourceJurorKeys = Object.keys(sourceFight.jurors);
				const destinationJurorKeys = Object.keys(destinationFight.jurors);
				let jurorToMove = {} as Juror;
				sourceJurorKeys.forEach((key) => {
					const juror = sourceFight.jurors[key];
					if (juror.position === oldPosition) {
						jurorToMove = sourceFight.jurors[key] as Juror;
						delete sourceFight.jurors[key];
					} else if (juror.position > oldPosition) {
						sourceFight.jurors[key].position = sourceFight.jurors[key].position - 1;
					}
				});
				destinationJurorKeys.forEach((key) => {
					const juror = destinationFight.jurors[key];
					if (juror.position >= newPosition) {
						destinationFight.jurors[key].position = destinationFight.jurors[key].position + 1;
					}
				});
				jurorToMove.position = newPosition;
				destinationFight.jurors[jurorToMove.id] = jurorToMove;
				fights[sourceFightIndex] = sourceFight;
				fights[destinationFightIndex] = destinationFight;
				this.setState({ fights });
				try {
					const sourceOrder = getOrder(sourceFight);
					const destinationOrder = getOrder(destinationFight);

					const resAdd = await addJurorToFight(
						destinationFight.id,
						jurorToMove.id,
						jurorToMove.position
					);
					const resDelete = await deleteJurorFromFight(sourceFight.id, jurorToMove.id);
					if (resAdd.success && resDelete.success) {
						const resDestinationOrder = await sendJurorOrderBackend(destinationOrder);
						if (!resDestinationOrder.success) {
							console.log("reverting to old juror Order");
							throw new Error("not successfull");
						}
						if (sourceOrder.jurorOrder.length > 0) {
							const resSourceOrder = await sendJurorOrderBackend(sourceOrder);
							if (!resSourceOrder.success) {
								console.log("reverting to old juror Order");
								throw new Error("not successfull");
							}
						}
					} else {
						throw new Error("not successfull");
					}
				} catch (e) {
					fights[sourceFightIndex] = backupSourceFight;
					fights[destinationFightIndex] = backupDestinationFight;
					this.setState({ fights });
				}
			}
		}
	};

	private getRound = (round: number) => {
		return (
			<div>
				<H1 {...styles.title}>Round {round}</H1>
				<div {...styles.container}>
					<DragDropContext onDragEnd={(res) => this.onDragEnd(res, round)}>
						<div key={"unsassigned" + round} {...styles.element} {...styles.dragNDropContent}>
							<Card>
								<H5>Unassigned Jurors</H5>
								<Droppable droppableId={"unassigned" + round}>
									{(provided, snapshot) =>
										(
											<div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
												{_.sortBy(this.state.allJurors, (j) => _.trim(j.lastname))
													.filter((juror) => this.jurorNotInRound(juror, round))
													.map((item, index) => (
														<Draggable key={item.id} draggableId={String(item.id)} index={index}>
															{(provided2, snapshot2) =>
																(
																	<div
																		ref={provided2.innerRef}
																		{...provided2.draggableProps}
																		{...provided2.dragHandleProps}
																		style={getItemStyle(
																			snapshot2.isDragging,
																			provided2.draggableProps.style
																		)}
																	>
																		{item.lastname} {item.firstname}
																	</div>
																) as React.ReactElement<HTMLElement>
															}
														</Draggable>
													))}
												{provided.placeholder}
											</div>
										) as React.ReactElement<HTMLElement>
									}
								</Droppable>
							</Card>
						</div>
						{_.sortBy(([] as Fight[]).concat(this.state.fights), ["name"])
							.filter((fight) => fight.fightnr === round)
							.map((fight: Fight, i: number) => (
								<div key={fight.id} {...styles.element} {...styles.dragNDropContent}>
									<Card>
										<H5>{fight.name}</H5>
										<Droppable droppableId={fight.name}>
											{(provided, snapshot) =>
												(
													<div
														ref={provided.innerRef}
														style={getListStyle(snapshot.isDraggingOver)}
													>
														{Object.keys(fight.jurors)
															.map((key) => fight.jurors[key])
															.sort((a, b) => a.position - b.position)
															.map((item, index) => (
																<Draggable
																	key={item.id}
																	draggableId={String(item.id)}
																	index={index}
																>
																	{(provided2, snapshot2) =>
																		(
																			<div
																				ref={provided2.innerRef}
																				{...provided2.draggableProps}
																				{...provided2.dragHandleProps}
																				style={getItemStyle(
																					snapshot2.isDragging,
																					provided2.draggableProps.style
																				)}
																			>
																				{item.lastname} {item.firstname}
																			</div>
																		) as React.ReactElement<HTMLElement>
																	}
																</Draggable>
															))}
														{provided.placeholder}
													</div>
												) as React.ReactElement<HTMLElement>
											}
										</Droppable>
									</Card>
								</div>
							))}
					</DragDropContext>
				</div>
			</div>
		);
	};

	public render() {
		return (
			<div>
				<div>{this.state.allJurors.length === 0 ? <Loading /> : this.getRound(1)}</div>
				<div>{this.state.allJurors.length === 0 ? <div /> : this.getRound(2)}</div>
				<div>{this.state.allJurors.length === 0 ? <div /> : this.getRound(3)}</div>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/Backend/JuryPlanManager.tsx